import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const skillData = {
    ML: {
        title: 'Machine Learning & Modeling',
        summary: 'I use classical ML and deep learning to analyze behavioral, neural, and sensor data in neuroengineering experiments.',
        skills: [
            { name: 'Deep Learning', tools: 'YOLOv5/8/11, CNNs, TensorFlow, Keras', level: 90, context: 'Object detection in biomedical imaging' },
            { name: 'Classical ML', tools: 'SVM, Random Forest, XGBoost', level: 85, context: 'Classification of neural signals' },
            { name: 'Computer Vision', tools: 'Neural Decoding, Image Processing', level: 80, context: 'Analyzing experimental video data' },
            { name: 'Explainability', tools: 'SHAP, LIME, Model Evaluation', level: 75, context: 'Interpreting model decisions' },
        ]
    },
    Programming: {
        title: 'Programming & Software',
        summary: 'I build robust software for data analysis, simulation, and web interfaces using a variety of languages.',
        skills: [
            { name: 'Python Stack', tools: 'Pandas, NumPy, SciPy', level: 95, context: 'Primary language for data analysis' },
            { name: 'Web Development', tools: 'React.js, HTML/CSS', level: 80, context: 'Building interactive portfolios and dashboards' },
            { name: 'Scientific Computing', tools: 'MATLAB, R, C', level: 85, context: 'Signal processing and statistical modeling' },
            { name: 'Data & Tooling', tools: 'SQL, Git, Jupyter', level: 75, context: 'Version control and data management' },
        ]
    },
    WetLab: {
        title: 'Wet Lab & Biomedical',
        summary: 'I have hands-on experience with in-vivo and in-vitro techniques for neuroprosthetics research.',
        skills: [
            { name: 'Neurophysiology', tools: 'EEG, EIS, Nova Autolab', level: 90, context: 'Recording neural activity' },
            { name: 'Animal Behavior', tools: 'Rat/Mouse In-vivo Work', level: 85, context: 'Designing and running experiments' },
            { name: 'Microscopy', tools: 'Tissue Processing, Imaging', level: 80, context: 'Histology and structural analysis' },
            { name: 'Hardware', tools: 'Soldering, Sensors, Prototyping', level: 75, context: 'Building experimental setups' },
        ]
    },
    Quant: {
        title: 'Quantitative Methods',
        summary: 'I apply mathematical and statistical methods to model complex biological systems.',
        skills: [
            { name: 'Simulation', tools: 'Monte Carlo, Dynamical Systems', level: 85, context: 'Modeling neural dynamics' },
            { name: 'Signal Processing', tools: 'Filtering, FFT, Wavelets', level: 90, context: 'Cleaning and analyzing EEG data' },
            { name: 'Statistical Modeling', tools: 'Hypothesis Testing, Regression', level: 80, context: 'Validating experimental results' },
            { name: 'Optimization', tools: 'Control Systems, Param Tuning', level: 75, context: 'Improving system performance' },
        ]
    },
    Leadership: {
        title: 'Leadership & Strategy',
        summary: 'I lead interdisciplinary teams by combining strategic planning, technical fluency, and human-centered decision making.',
        skills: [
            { name: 'Strategic Thinking', tools: 'Google Developer Student Club Lead', level: 95, context: 'Driving technical direction & execution' },
            { name: 'Adaptability', tools: 'Multi-domain Fluency', level: 90, context: 'Integrating insights across Wet Lab, ML, and Eng' },
            { name: 'Collaboration', tools: 'Interdisciplinary Bridging', level: 90, context: 'Connecting diverse contributors to solve problems' },
            { name: 'Resilience', tools: 'Conflict Resolution, Crisis Mgmt', level: 85, context: 'Maintaining stability in high-pressure situations' },
        ]
    }
};

const SkillDetailsPanel = ({ selectedCategory }) => {
    if (!selectedCategory) {
        return (
            <div className="details-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <div>
                    <h3>Skill Overview</h3>
                    <p>I work at the intersection of neural interfaces, wet lab, and machine learning, using programming as the glue.</p>
                    <p style={{ marginTop: '1rem', color: '#4db5ff' }}>Hover or click a node to explore details.</p>
                </div>
            </div>
        );
    }

    const data = skillData[selectedCategory];

    return (
        <AnimatePresence mode='wait'>
            <motion.div
                key={selectedCategory}
                className="details-panel"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
            >
                <div className="details-header">
                    <h3>{data.title}</h3>
                    <p>{data.summary}</p>
                </div>

                <div className="skills-list">
                    {data.skills.map((skill, index) => (
                        <div key={index} className="skill-bar-container">
                            <div className="skill-bar-header">
                                <span className="skill-name">{skill.name}</span>
                                <span className="skill-tools">{skill.tools}</span>
                            </div>
                            <div className="skill-bar-bg">
                                <motion.div
                                    className="skill-bar-fill"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${skill.level}%` }}
                                    transition={{ duration: 1, delay: 0.2 }}
                                />
                            </div>
                            <div className="skill-context">{skill.context}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SkillDetailsPanel;
